import React, { useMemo } from "react";
import { FiUser, FiPackage, FiFileText, FiHash, FiDollarSign, FiCalendar, FiFlag, FiUsers } from "react-icons/fi";
import { money } from "../../utils/formatters";

// ── Payment mode helpers ─────────────────────────────────────
const PAYMENT_MODES = [
  { value: "none",    label: "No payment now" },
  { value: "half",    label: "Half payment (50%)" },
  { value: "full",    label: "Full payment (100%)" },
  { value: "custom",  label: "Custom amount" },
];

const ORDER_STATUSES = [
  "pending", "design_review", "approved",
  "in_production", "quality_check", "ready", "delivered", "cancelled",
];

const PAYMENT_METHODS = [
  { value: "cash",          label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card",          label: "Card" },
  { value: "mobile_money",  label: "Mobile Money" },
];

const OrderFormPanel = ({
  editingId = null,
  form,
  customers,
  employees,
  submitError,
  onSubmit,
  onChange,
  onCancel,
}) => {
  const totalPrice = useMemo(() => {
    const qty   = Number(form.quantity  || 0);
    const price = Number(form.unit_price || 0);
    return qty * price;
  }, [form.quantity, form.unit_price]);

  // Derive paid_amount from payment_mode
  const resolvedPaidAmount = useMemo(() => {
    if (form.payment_mode === "full")   return totalPrice;
    if (form.payment_mode === "half")   return Math.round((totalPrice / 2) * 100) / 100;
    if (form.payment_mode === "custom") return Number(form.paid_amount || 0);
    return 0;
  }, [form.payment_mode, form.paid_amount, totalPrice]);

  const balance = Math.max(0, totalPrice - resolvedPaidAmount);

  const handleChange = (field, value) => {
    const next = { ...form, [field]: value };

    // Auto-fill paid_amount when mode changes
    if (field === "payment_mode") {
      if (value === "full")   next.paid_amount = totalPrice;
      else if (value === "half") next.paid_amount = Math.round((totalPrice / 2) * 100) / 100;
      else if (value === "none") next.paid_amount = 0;
    }

    // Recalculate when qty/price changes
    if (field === "quantity" || field === "unit_price") {
      const qty   = Number(field === "quantity"   ? value : form.quantity   || 0);
      const price = Number(field === "unit_price" ? value : form.unit_price || 0);
      const total = qty * price;
      if (next.payment_mode === "full")  next.paid_amount = total;
      if (next.payment_mode === "half")  next.paid_amount = Math.round((total / 2) * 100) / 100;
    }

    onChange(next);
  };

  return (
    <form className="panel form-panel orders-form-panel form-modern" onSubmit={onSubmit}>
      <div className="form-section">
        <div className="form-section__header">
          <h3 className="form-section__title">{editingId ? "Edit Order" : "Create New Order"}</h3>
          <p className="form-section__subtitle">{editingId ? "Update order details and payment information" : "Add a new printing order with customer and product details"}</p>
        </div>

        {submitError && (
          <div className="form-alert form-alert--danger">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{submitError}</span>
          </div>
        )}

        <div className="form-grid form-grid--2">
          {/* ── Customer ── */}
          <div className="form-group form-group--full">
            <label className="form-label form-label--required">
              <FiUser /> Customer
            </label>
            <select
              className="form-select"
              value={form.customer_id}
              onChange={(e) => handleChange("customer_id", e.target.value ? Number(e.target.value) : "")}
              required
            >
              <option value="">Select customer</option>
              {customers?.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* ── Title & product type ── */}
          <div className="form-group">
            <label className="form-label form-label--required">
              <FiFileText /> Order Title
            </label>
            <div className="form-input-group">
              <span className="form-input-group__icon"><FiFileText /></span>
              <input
                className="form-input"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g. Spring menu flyers"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label form-label--required">
              <FiPackage /> Product Type
            </label>
            <div className="form-input-group">
              <span className="form-input-group__icon"><FiPackage /></span>
              <input
                className="form-input"
                value={form.product_type}
                onChange={(e) => handleChange("product_type", e.target.value)}
                placeholder="e.g. Flyers, Banners, Business Cards"
                required
              />
            </div>
          </div>

          {/* ── Description ── */}
          <div className="form-group form-group--full">
            <label className="form-label">
              <FiFileText /> Description
              <span className="form-label__hint">(Specs, finish, paper type, notes)</span>
            </label>
            <textarea
              className="form-textarea"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Add detailed specifications and requirements"
              rows="3"
            />
          </div>

          {/* ── Qty & price ── */}
          <div className="form-group">
            <label className="form-label form-label--required">
              <FiHash /> Quantity
            </label>
            <div className="form-input-group">
              <span className="form-input-group__icon"><FiHash /></span>
              <input
                className="form-input"
                type="number"
                min="1"
                step="1"
                value={form.quantity}
                onChange={(e) => handleChange("quantity", e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="e.g. 3000"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label form-label--required">
              <FiDollarSign /> Unit Price (ETB)
            </label>
            <div className="form-input-group">
              <span className="form-input-group__icon"><FiDollarSign /></span>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.01"
                value={form.unit_price}
                onChange={(e) => handleChange("unit_price", e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="e.g. 0.38"
                required
              />
            </div>
          </div>
        </div>

        {/* ── Live total ── */}
        {totalPrice > 0 && (
          <div className="order-total-preview" style={{
            background: "var(--brand-light, #fdf0e8)",
            borderRadius: "8px",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            border: "2px solid var(--brand, #e8702a)"
          }}>
            <span style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-primary)" }}>Order Total</span>
            <strong style={{ fontSize: "1.5rem", color: "var(--brand, #e8702a)" }}>{money(totalPrice)}</strong>
          </div>
        )}

        {/* ── Payment section ── */}
        <div className="order-payment-section" style={{ 
          background: "var(--bg-subtle, #f9fafb)", 
          borderRadius: "8px", 
          padding: "20px",
          marginBottom: "24px"
        }}>
          <p className="order-payment-section__label" style={{ 
            fontSize: "0.875rem", 
            fontWeight: 600, 
            marginBottom: "12px",
            color: "var(--text-primary)"
          }}>
            Payment at Order Creation
          </p>

          <div className="payment-mode-grid" style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", 
            gap: "10px",
            marginBottom: "16px"
          }}>
            {PAYMENT_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                className={`payment-mode-btn${form.payment_mode === mode.value ? " payment-mode-btn--active" : ""}`}
                onClick={() => handleChange("payment_mode", mode.value)}
                style={{
                  padding: "12px 16px",
                  border: form.payment_mode === mode.value ? "2px solid var(--brand, #e8702a)" : "2px solid var(--border, #e5e7eb)",
                  borderRadius: "8px",
                  background: form.payment_mode === mode.value ? "var(--brand-light, #fdf0e8)" : "white",
                  color: form.payment_mode === mode.value ? "var(--brand-deep, #8f3412)" : "var(--text-primary)",
                  fontWeight: form.payment_mode === mode.value ? 600 : 500,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {form.payment_mode === "custom" && (
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label className="form-label">
                <FiDollarSign /> Custom Amount Paid
              </label>
              <div className="form-input-group">
                <span className="form-input-group__icon"><FiDollarSign /></span>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.01"
                  max={totalPrice || undefined}
                  value={form.paid_amount}
                  onChange={(e) => handleChange("paid_amount", e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Enter amount paid"
                />
              </div>
            </div>
          )}

          {form.payment_mode && form.payment_mode !== "none" && (
            <div className="form-group">
              <label className="form-label">
                <FiDollarSign /> Payment Method
              </label>
              <select
                className="form-select"
                value={form.payment_method || "cash"}
                onChange={(e) => handleChange("payment_method", e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Payment summary */}
          {form.payment_mode && form.payment_mode !== "none" && totalPrice > 0 && (
            <div className="payment-summary" style={{
              marginTop: "16px",
              padding: "16px",
              background: "white",
              borderRadius: "8px",
              border: "1px solid var(--border, #e5e7eb)"
            }}>
              <div className="payment-summary__row" style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: "var(--text-muted, #888)" }}>Total</span>
                <strong>{money(totalPrice)}</strong>
              </div>
              <div className="payment-summary__row" style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ color: "var(--text-muted, #888)" }}>Paid now</span>
                <strong style={{ color: "var(--success, #10b981)" }}>{money(resolvedPaidAmount)}</strong>
              </div>
              <div className="payment-summary__row payment-summary__row--balance" style={{ 
                display: "flex", 
                justifyContent: "space-between",
                paddingTop: "8px",
                borderTop: "1px solid var(--border, #e5e7eb)"
              }}>
                <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                <strong style={{ color: balance > 0 ? "var(--danger, #b93826)" : "var(--success, #10b981)", fontSize: "1.125rem" }}>
                  {money(balance)}
                </strong>
              </div>
            </div>
          )}
        </div>

        <div className="form-grid form-grid--2">
          {/* ── Priority & deadline ── */}
          <div className="form-group">
            <label className="form-label">
              <FiFlag /> Priority
            </label>
            <select
              className="form-select"
              value={form.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
            >
              <option value="">Select priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              <FiCalendar /> Deadline
            </label>
            <div className="form-input-group">
              <span className="form-input-group__icon"><FiCalendar /></span>
              <input
                className="form-input"
                type="date"
                value={form.deadline}
                onChange={(e) => handleChange("deadline", e.target.value)}
              />
            </div>
          </div>

          {/* ── Status (edit only) & employee ── */}
          {editingId && (
            <div className="form-group">
              <label className="form-label">Order Status</label>
              <select
                className="form-select"
                value={form.order_status || ""}
                onChange={(e) => handleChange("order_status", e.target.value)}
              >
                {ORDER_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              <FiUsers /> Assign Employee
            </label>
            <select
              className="form-select"
              value={form.assigned_employee_id}
              onChange={(e) => handleChange("assigned_employee_id", e.target.value ? Number(e.target.value) : "")}
            >
              <option value="">Unassigned</option>
              {employees?.data?.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} — {emp.role}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button className="btn-secondary" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-primary" type="submit">
            {editingId ? "Update Order" : "Create Order"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default OrderFormPanel;
